import { useCallback, useEffect, useState } from 'react';

// Define the shape of the data returned by the hook
const initialHookState = {
  date: null,
  year: null,
  isLoading: true,
  error: null,
};

// const DEFAULT_DATE_API_URL = 'https://worldtimeapi.org/api//timezone/America/New_York';
const DEFAULT_DATE_API_URL = `${import.meta.env.VITE_AUTH_DOMAIN || ''}/api/v1/utils/time/`;

/**
 * Custom React hook to fetch the current date from a remote service.
 * Includes loading, success, and error states.
 *
 * @param {string} dateApiUrl - The URL of the service endpoint to fetch the date from.
 * @returns {{date: string | null, isLoading: boolean, error: string | null}}
 */
export const useRealDate = (dateApiUrl) => {
  const [state, setState] = useState(initialHookState);

  // Use useCallback to memoize the function, which is good practice for async actions
  const fetchDate = useCallback(async () => {
    setState({ ...initialHookState, isLoading: true }); // Start loading

    try {
      const response = await fetch(dateApiUrl || DEFAULT_DATE_API_URL);

      if (!response.ok) {
        // Throw an error if the HTTP status is not 2xx
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();


      const realDateText = data?.datetime_iso || new Date().toISOString(); // Fallback for safety
      const realDate = new Date(realDateText);
      const year = realDate.getFullYear();

      setState({
        date: realDateText,
        year: year,
        isLoading: false,
        error: null,
      });

    }
    catch (err) {
      // Catch any network or parsing errors (try...catch)
      console.error('Failed to fetch real date:', err);

      setState({
        date: null,
        isLoading: false,
        error: err.message || 'An unknown error occurred while fetching the date.',
      });
    }
  }, [dateApiUrl]); // Re-create fetchDate if dateApiUrl changes

  // useEffect runs the fetchDate function when the component mounts.
  // setState happens inside the async fetchDate callback, not synchronously in the effect body — the
  // react-hooks/set-state-in-effect rule can't distinguish those cases, so suppress it here.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchDate();
  }, [fetchDate]);

  // Return the current state for the consuming component
  return state;
};
